import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { DAYS_FR, addDays, computeDayMinutes, minutesToHM, isoWeekLabel, toISODate } from "@/lib/time";
import { CheckCircle2, XCircle, User } from "lucide-react";

export default function ValidationPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // {timesheet, summary, user, action}
  const [comment, setComment] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/timesheets/pending");
      setItems(data);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const doAction = async () => {
    if (!selected) return;
    const url = selected.action === "approve" ? "/timesheets/approve" : "/timesheets/reject";
    try {
      await api.post(url, {
        user_id: selected.user.id,
        week_start: selected.timesheet.week_start,
        comment,
      });
      toast.success(selected.action === "approve" ? "Semaine validée" : "Semaine refusée");
      setSelected(null); setComment("");
      load();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="validation-page">
      <header>
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Responsable</div>
        <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">Validation des semaines</h1>
        <p className="text-sm text-muted-foreground mt-1">Vérifiez et validez les relevés d'heures soumis par votre équipe.</p>
      </header>

      {loading && <div className="py-10 text-muted-foreground">Chargement...</div>}

      {!loading && items.length === 0 && (
        <Card className="border-border/60 shadow-warm">
          <CardContent className="p-10 text-center">
            <CheckCircle2 className="h-10 w-10 text-primary mx-auto mb-3" />
            <p className="font-medium">Rien à valider</p>
            <p className="text-sm text-muted-foreground">Toutes les semaines de votre équipe sont à jour.</p>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((it) => {
          const monday = new Date(it.timesheet.week_start);
          return (
            <Card key={it.timesheet.id} className="border-border/60 shadow-warm fade-up" data-testid={`pending-card-${it.user.id}-${it.timesheet.week_start}`}>
              <CardContent className="p-5 space-y-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 font-display text-lg font-semibold">
                      <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center">
                        <User className="h-4 w-4 text-accent-foreground" />
                      </div>
                      {it.user.name}
                    </div>
                    <div className="text-xs text-muted-foreground">{it.user.email}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">{isoWeekLabel(it.timesheet.week_start)}</div>
                    <div className="font-display text-2xl font-semibold tabular-nums">{it.summary.worked_hours.toFixed(2)} h</div>
                    <div className={`text-xs font-medium ${it.summary.delta_hours >= 0 ? "text-primary" : "text-destructive"}`}>
                      {it.summary.delta_hours >= 0 ? "+" : ""}{it.summary.delta_hours.toFixed(2)} h récup
                    </div>
                  </div>
                </div>

                <div className="text-sm border border-border/60 rounded-lg overflow-hidden">
                  {DAYS_FR.map((d, i) => {
                    const iso = toISODate(addDays(monday, i));
                    const e = (it.timesheet.entries || []).find(x => x.date === iso) || {};
                    const m = computeDayMinutes(e);
                    return (
                      <div key={iso} className="grid grid-cols-6 gap-2 px-3 py-1.5 border-b last:border-b-0 border-border/40 text-xs">
                        <div className="font-medium">{d.slice(0, 3)}</div>
                        <div className="text-muted-foreground tabular-nums">{e.arrival || "-"}</div>
                        <div className="text-muted-foreground tabular-nums">{e.lunch_start || "-"}</div>
                        <div className="text-muted-foreground tabular-nums">{e.lunch_end || "-"}</div>
                        <div className="text-muted-foreground tabular-nums">{e.departure || "-"}</div>
                        <div className="text-right font-medium tabular-nums">{minutesToHM(m)}</div>
                      </div>
                    );
                  })}
                </div>

                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    className="rounded-full border-destructive/30 text-destructive hover:bg-destructive/10"
                    onClick={() => { setSelected({ ...it, action: "reject" }); setComment(""); }}
                    data-testid={`reject-${it.user.id}`}
                  >
                    <XCircle className="h-4 w-4 mr-2" /> Refuser
                  </Button>
                  <Button
                    className="rounded-full active:scale-95 bg-secondary text-secondary-foreground hover:bg-secondary/90"
                    onClick={() => { setSelected({ ...it, action: "approve" }); setComment(""); }}
                    data-testid={`approve-${it.user.id}`}
                  >
                    <CheckCircle2 className="h-4 w-4 mr-2" /> Valider
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) setSelected(null); }}>
        <DialogContent data-testid="review-dialog">
          <DialogHeader>
            <DialogTitle>
              {selected?.action === "approve" ? "Valider la semaine" : "Refuser la semaine"}
              {" "}— {selected?.user?.name}
            </DialogTitle>
          </DialogHeader>
          <Textarea
            placeholder="Commentaire (optionnel)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            data-testid="review-comment"
            rows={4}
          />
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setSelected(null)}>Annuler</Button>
            <Button className="rounded-full active:scale-95" onClick={doAction} data-testid="confirm-review-btn">
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
